import { Component, Input, OnInit } from '@angular/core';
import { Doctor } from 'src/app/entities/user.entity/doctor.entity';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DoctorPatientsService } from 'src/app/services/doctor-patients.service/doctor-patients.service';
import { User } from 'src/app/entities/user.entity';
import { AuthService } from 'src/app/services/auth.service/auth.service';

// --- Types calqués sur la réponse API réelle (Page Spring Boot) ---

interface ParamSanteDef {
  paramId: string;
  paramLibelle: string;
  paramUnite: string | null;
  paramFamille: string | null;
  paramType: string; // numerique | oui_non | chaine_de_caractere
}

interface ParamSanteValue {
  paramSanteValueId: string;
  note: string;
  paramSante: ParamSanteDef;
  value: string;
  value_OD: string | null;
  value_OG: string | null;
}

interface Motif {
  id: string;
  name: string;
  color: string;
}

interface ConsultationHistoric {
  id: string;
  date: number;
  time: string;
  diagnostic: string;
  gender: string;
  motifs: Motif[];
  paramSanteValue: ParamSanteValue[];
}

@Component({
  selector: 'app-historique-paramtere-sante',
  templateUrl: './historique-paramtere-sante.component.html',
  styleUrls: ['./historique-paramtere-sante.component.css'],
})
export class HistoriqueParamtereSanteComponent implements OnInit {
  @Input() patientId: string = '';
  age: number = 0;

  patient: User = new User();

  patientHistorics: ConsultationHistoric[] = []; // slice affichée (page courante)
  allHistorics: ConsultationHistoric[] = []; // liste complète récupérée
  paramList: { id: string; label: string; unit: string; type: string }[] = [];

  // --- Pagination ---
  currentPage = 0; // 0-indexed, comme Spring Data
  pageSize = 10;
  totalPages = 0;
  totalElements = 0;
  loading = false;

  get doctor(): Doctor {
    return new Doctor(this.authService.getCurrentUser()!);
  }

  constructor(
    private router: Router,
    private doctorPatientsService: DoctorPatientsService,
    private toastrService: ToastrService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.patientId = this.router.url.split('/')[
      this.router.url.split('/').indexOf('patients') + 1
    ];
    this.doctorPatientsService.one(this.patientId).subscribe((res) => {
      this.patient = User.fromResponse(res);
      this.calculAge(this.patient.birthDate);
    });
    this.loadHistoriqueConsultation(0);
  }

  calculAge(age: Date): void {
    let d = new Date(age);
    this.age = new Date().getFullYear() - d.getFullYear();
    var m = new Date().getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && new Date().getDate() < d.getDate())) {
      this.age = this.age - 1;
    }
  }

  /**
   * Charge l'historique complet des paramètres santé une seule fois.
   * NB: l'API historicsHealth ne paginant pas de façon fiable côté serveur,
   * la pagination (10 par page) est gérée ici côté client sur la liste complète.
   */
  loadHistoriqueConsultation(page: number): void {
    this.loading = true;
    this.doctorPatientsService
      // 3e paramètre conservé tel que dans l'appel d'origine (timestamp de référence),
      // la pagination par 10 est désormais gérée localement via setPage().
      .historicsHealth(this.patientId, 0, Date.now())
      .subscribe(
        (res: any) => {
          const pageData = res?.data ?? res;
          const content: ConsultationHistoric[] = pageData?.content ?? res ?? [];

          this.allHistorics = (content || []).sort((a, b) => b.date - a.date);
          this.totalElements = this.allHistorics.length;
          this.totalPages = Math.max(1, Math.ceil(this.totalElements / this.pageSize));

          this.setPage(page);
          this.loading = false;
        },
        (error) => {
          this.toastrService.error("Impossible de charger l'historique du patient");
          this.loading = false;
        }
      );
  }

  /** Découpe la liste complète pour n'afficher que la page demandée. */
  private setPage(page: number): void {
    this.currentPage = Math.min(Math.max(page, 0), this.totalPages - 1);
    const start = this.currentPage * this.pageSize;
    this.patientHistorics = this.allHistorics.slice(start, start + this.pageSize);
    this.buildParamList();
  }

  /** Construit la liste plate des paramètres à afficher, dans l'ordre où ils
   *  apparaissent dans la réponse API (ordre de `paramSanteValue` de chaque
   *  consultation), sans regroupement par famille. Ne garde que les paramètres
   *  ayant au moins une valeur renseignée sur la page affichée. */
  private buildParamList(): void {
    const seen = new Map<string, { id: string; label: string; unit: string; type: string }>();

    this.patientHistorics.forEach((h) => {
      (h.paramSanteValue || []).forEach((p) => {
        const hasValue =
          (p.value && p.value.trim() !== '') ||
          (p.value_OD && p.value_OD.trim() !== '') ||
          (p.value_OG && p.value_OG.trim() !== '');
        if (!hasValue || !p.paramSante) return;
        if (seen.has(p.paramSante.paramId)) return;

        const unit =
          p.paramSante.paramUnite && p.paramSante.paramUnite !== '-'
            ? p.paramSante.paramUnite
            : '';
        seen.set(p.paramSante.paramId, {
          id: p.paramSante.paramId,
          label: p.paramSante.paramLibelle,
          unit,
          type: p.paramSante.paramType,
        });
      });
    });

    this.paramList = Array.from(seen.values());
  }

  /** Retourne la valeur affichable d'un paramètre pour une consultation donnée. */
  getParamValue(historic: ConsultationHistoric, paramId: string): string {
    const match = (historic.paramSanteValue || []).find(
      (p) => p.paramSante?.paramId === paramId
    );
    if (!match) return '—';

    if (match.paramSante.paramType === 'oui_non') {
      if (match.value === 'true') return 'Oui';
      if (match.value === 'false') return 'Non';
      return '—';
    }

    if (match.value_OD || match.value_OG) {
      const od = match.value_OD || '—';
      const og = match.value_OG || '—';
      return `OD ${od} / OG ${og}`;
    }

    if (!match.value) return '—';
    const unit =
      match.paramSante.paramUnite && match.paramSante.paramUnite !== '-'
        ? ' ' + match.paramSante.paramUnite
        : '';
    return `${match.value}${unit}`;
  }

  // --- Pagination handlers (100% côté client, sur allHistorics déjà chargé) ---
  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.currentPage) return;
    this.setPage(page);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  // --- Impression ---
  async print() {
    this.printDirectly();
  }

  /** Calcule, pour un lot donné de consultations, la liste plate des paramètres
   *  ayant au moins une valeur (même logique que buildParamList, mais réutilisable
   *  pour n'importe quel sous-ensemble de consultations). */
  private computeParamListFor(
    historics: ConsultationHistoric[]
  ): { id: string; label: string; unit: string; type: string }[] {
    const seen = new Map<string, { id: string; label: string; unit: string; type: string }>();
    historics.forEach((h) => {
      (h.paramSanteValue || []).forEach((p) => {
        const hasValue =
          (p.value && p.value.trim() !== '') ||
          (p.value_OD && p.value_OD.trim() !== '') ||
          (p.value_OG && p.value_OG.trim() !== '');
        if (!hasValue || !p.paramSante || seen.has(p.paramSante.paramId)) return;
        const unit =
          p.paramSante.paramUnite && p.paramSante.paramUnite !== '-'
            ? p.paramSante.paramUnite
            : '';
        seen.set(p.paramSante.paramId, {
          id: p.paramSante.paramId,
          label: p.paramSante.paramLibelle,
          unit,
          type: p.paramSante.paramType,
        });
      });
    });
    return Array.from(seen.values());
  }

  private escapeHtml(text: string | null | undefined): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private formatDate(ts: number): string {
    const d = new Date(ts);
    return d.toLocaleDateString('fr-FR');
  }

  /** Génère le HTML d'une page d'impression (un tableau de 10 consultations max). */
  private buildPrintPageHtml(chunk: ConsultationHistoric[]): string {
    const params = this.computeParamListFor(chunk);

    const headerCells = chunk
      .map((h) => {
        const motifs = (h.motifs || [])
          .map(
            (m) =>
              `<span style="display:inline-block;color:#fff;font-size:7.5pt;font-weight:600;padding:1px 6px;border-radius:999px;background:${this.escapeHtml(
                m.color
              )};margin:1px;">${this.escapeHtml(m.name)}</span>`
          )
          .join(' ');
        return `<th>
            <div style="font-weight:700;">${this.formatDate(h.date)}</div>
            <div style="font-weight:400;font-size:7.5pt;">${this.escapeHtml(h.time)}</div>
            <div style="margin-top:4px;">${motifs}</div>
          </th>`;
      })
      .join('');

    const rows = params
      .map((param) => {
        const cells = chunk
          .map((h) => `<td>${this.escapeHtml(this.getParamValue(h, param.id))}</td>`)
          .join('');
        const unit = param.unit ? ` (${this.escapeHtml(param.unit)})` : '';
        return `<tr><td style="text-align:left;font-weight:600;">${this.escapeHtml(
          param.label
        )}${unit}</td>${cells}</tr>`;
      })
      .join('');

    return `
      <table>
        <thead>
          <tr><th>Paramètre</th>${headerCells}</tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

printDirectly() {
    if (this.allHistorics.length === 0) {
      return;
    }

    const chunks: ConsultationHistoric[][] = [];
    for (let i = 0; i < this.allHistorics.length; i += this.pageSize) {
      chunks.push(this.allHistorics.slice(i, i + this.pageSize));
    }

    const headerHtml = `
      <div class="print-header">
        <h4>Dossier Patient — Historique paramètres santé</h4>
        <p><strong>Nom :</strong> ${this.escapeHtml(this.patient.getFullName())} &nbsp; | &nbsp;
           <strong>Naissance :</strong> ${this.formatDate(
             new Date(this.patient.birthDate).getTime()
           )} &nbsp; | &nbsp;
           <strong>Âge :</strong> ${this.age} ans</p>
        ${this.patient.address ? `<p><strong>Adresse :</strong> ${this.escapeHtml(this.patient.address)}</p>` : ''}
      </div>
    `;

    // Footer désormais généré pour CHAQUE page, avec le numéro de page inclus
    // const buildFooterHtml = (pageIndex: number, totalPages: number): string => `
    //   <div class="print-footer">
    //     <hr />
    //     <div class="print-footer-content">
    //       <div class="print-footer-doctor">
    //         <p><strong>Docteur :</strong> ${this.escapeHtml(this.doctor.firstName)} ${this.escapeHtml(
    //   this.doctor.lastName
    // )}</p>
    //         ${this.doctor.address ? `<p><strong>Adresse :</strong> ${this.escapeHtml(this.doctor.address)}</p>` : ''}
    //         ${
    //           this.doctor.mobilePhone1
    //             ? `<p><strong>Téléphone :</strong> ${this.escapeHtml(this.doctor.mobilePhone1)}</p>`
    //             : ''
    //         }
    //       </div>
    //       <p class="page-indicator">Page ${pageIndex + 1} / ${totalPages}</p>
    //     </div>
    //   </div>
    // `;

    const pagesHtml = chunks
      .map((chunk, i) => {
        const isLast = i === chunks.length - 1;
        return `
          <div class="print-page" style="${isLast ? '' : 'page-break-after: always;'}">
            <div class="print-page-top">
              ${headerHtml}
              <div class="print-body">${this.buildPrintPageHtml(chunk)}</div>
            </div>
          </div>
        `;
                    // ${buildFooterHtml(i, chunks.length)}

      })
      .join('');

    const printWindow = window.open(
      '',
      '_blank',
      'width=1123,height=794,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes'
    );

    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="fr">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Impression - Historique paramètres santé</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            html, body { height: 100%; }
            body {
              font-family: Calibri, Arial, sans-serif;
              font-size: 9pt;
              color: #1f2933;
              margin: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-page {
              min-height: 190mm; /* hauteur utile A4 paysage - marges (297-2*10 -> retiré côté @page, ici hauteur = 210-2*10) */
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .print-header { text-align: center; }
            .print-header h4 { color: #008D93; margin-bottom: 4px; }
            .print-header p { margin: 2px 0; }
            .print-body { margin: 10px 0 16px; }
            table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            th, td {
              border: 1px solid #cbd2d9;
              padding: 4px 6px;
              text-align: center;
              word-wrap: break-word;
              font-size: 8pt;
            }
            thead th { background: #008D93 !important; color: #fff !important; }

            /* Footer répété en bas de CHAQUE page */
     .print-footer { margin-top: auto; }
            .print-footer hr { border: none; border-top: 1px solid #cbd2d9; margin-bottom: 6px; }
            .print-footer-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
            }
            .print-footer-doctor p { margin: 1px 0; font-size: 8pt; }
            .page-indicator { font-size: 8pt; color: #6b7684; margin: 4px 0 0; }
          </style>
        </head>
        <body>
          <div class="print-container">${pagesHtml}</div>
          <script type="text/javascript">
            window.onload = function() {
              try {
                window.focus();
                window.print();
                window.onafterprint = function() { window.close(); };
              } catch (e) {
                var el = document.createElement('div');
                el.style.padding = '20px';
                el.style.color = 'red';
                el.innerHTML = "Erreur d'impression: " + e.message;
                document.body.appendChild(el);
              }
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  }
}